import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import logoPng from "../../assets/images/logo.png";
import { getAuthMode, clearGuest } from "../../utils/authHelpers";
import { useCart } from "../../contexts/CartContext";
import http from "../../lib/http";
import guestHttp from "../../lib/guestHttp";
import "../../styles/Header.css";

const NAV_LINKS = [
  { to: "/",        label: "Home"    },
  { to: "/explore", label: "Explore" },
  { to: "/about",   label: "About"   },
  { to: "/contact", label: "Contact" },
];

/* ── Icons ── */
const MenuIcon         = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>;
const CloseIcon        = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const ArrowRightIcon   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>;
const SearchIcon       = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;
const ArrowUpRightIcon = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M7 17l9.2-9.2M17 17V7H7"/></svg>;
const CartIconSvg      = () => <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>;
const ExitIcon         = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>;

/* ── Cart button — shows for guest and logged-in user ── */
const CartButton = ({ count, onClick }) => (
  <>
    <style>{`
      .hdr-cart-btn {
        position: relative;
        background: none;
        border: none;
        cursor: pointer;
        padding: 6px;
        color: var(--hdr-text, #0e3864);
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 8px;
        transition: background 0.14s, color 0.14s;
      }
      .hdr-cart-btn:hover {
        background: rgba(247,115,33,0.08);
        color: #f77321;
      }
      .hdr-cart-badge {
        position: absolute;
        top: 0px;
        right: 0px;
        background: #f77321;
        color: #fff;
        font-size: 9px;
        font-weight: 800;
        min-width: 16px;
        height: 16px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0 3px;
        line-height: 1;
        pointer-events: none;
      }
    `}</style>
    <button className="hdr-cart-btn" onClick={onClick} aria-label="Cart">
      <CartIconSvg />
      {count > 0 && (
        <span className="hdr-cart-badge">{count > 99 ? "99+" : count}</span>
      )}
    </button>
  </>
);

/* ── Guest pill — compact, just badge + two small buttons ── */
const GuestActions = ({ onLogin, onExit }) => (
  <>
    <style>{`
      .hdr-guest-strip {
        display: flex;
        align-items: center;
        gap: 6px;
      }
      .hdr-guest-pill {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        font-size: 11px;
        font-weight: 700;
        color: #6b7280;
        background: #f3f4f6;
        border: 1.5px solid #e5e7eb;
        border-radius: 20px;
        padding: 3px 10px 3px 7px;
        white-space: nowrap;
        letter-spacing: 0.01em;
      }
      .hdr-guest-pill-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #f77321;
        flex-shrink: 0;
      }
      .hdr-guest-login-btn {
        height: 30px;
        padding: 0 14px;
        background: #f77321;
        color: #fff;
        border: none;
        border-radius: 8px;
        font-size: 12.5px;
        font-weight: 700;
        cursor: pointer;
        white-space: nowrap;
        transition: background 0.14s, transform 0.1s;
        font-family: inherit;
      }
      .hdr-guest-login-btn:hover {
        background: #e0631a;
        transform: translateY(-1px);
      }
      .hdr-guest-exit-btn {
        height: 30px;
        padding: 0 10px;
        background: none;
        color: #6b7280;
        border: 1.5px solid #e5e7eb;
        border-radius: 8px;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 4px;
        white-space: nowrap;
        transition: border-color 0.14s, color 0.14s, background 0.14s;
        font-family: inherit;
      }
      .hdr-guest-exit-btn:hover {
        border-color: #ef4444;
        color: #ef4444;
        background: #fef2f2;
      }
    `}</style>
    <div className="hdr-guest-strip">
      <span className="hdr-guest-pill">
        <span className="hdr-guest-pill-dot" />
        Guest
      </span>
      <button className="hdr-guest-login-btn" onClick={onLogin}>Login</button>
      <button className="hdr-guest-exit-btn"  onClick={onExit}><ExitIcon /> Exit</button>
    </div>
  </>
);

export default function HeaderNonAuth() {
  const navigate = useNavigate();
  const loc      = useLocation();

  const [mobileOpen,    setMobileOpen]    = useState(false);
  const [authMode,      setAuthMode]      = useState(() => getAuthMode());
  const [scrolled,      setScrolled]      = useState(false);
  const [query,         setQuery]         = useState("");
  const [results,       setResults]       = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Guest cart count (local state — not from CartContext which is user-only)
  const [guestCartCount, setGuestCartCount] = useState(0);

  // User cart count from context (only available when user is logged in)
  let userCartCount = 0;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const ctx = useCart();
    userCartCount = ctx?.cartCount ?? 0;
  } catch {
    // CartContext not available in this header scope — fine
  }

  const searchRef = useRef(null);
  const isGuest   = authMode === "guest";
  const isUser    = authMode === "user";
  const showCart  = isGuest || isUser;
  const cartCount = isGuest ? guestCartCount : userCartCount;

  /* ── Auth sync ── */
  useEffect(() => {
    const sync = () => setAuthMode(getAuthMode());
    sync();
    window.addEventListener("auth:changed", sync);
    window.addEventListener("storage",      sync);
    return () => {
      window.removeEventListener("auth:changed", sync);
      window.removeEventListener("storage",      sync);
    };
  }, []);

  useEffect(() => { setAuthMode(getAuthMode()); }, [loc.pathname]);

  /* ── Fetch guest cart count ── */
  useEffect(() => {
    if (!isGuest) { setGuestCartCount(0); return; }
    let mounted = true;
    const fetchCount = async () => {
      try {
        const { data } = await guestHttp.get("/api/guest/cart");
        const items = Array.isArray(data?.data?.data)
          ? data.data.data
          : Array.isArray(data?.data)
            ? data.data
            : [];
        if (mounted) setGuestCartCount(items.length);
      } catch {
        if (mounted) setGuestCartCount(0);
      }
    };
    fetchCount();

    // Re-fetch whenever cart changes (addToCart dispatches this)
    const onCartChanged = () => fetchCount();
    window.addEventListener("cart:changed", onCartChanged);
    return () => {
      mounted = false;
      window.removeEventListener("cart:changed", onCartChanged);
    };
  }, [isGuest]);

  /* ── Scroll shadow ── */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* ── Body lock when mobile menu open ── */
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  /* ── Live search ── */
  useEffect(() => {
    const keyword = query.trim();
    const timer = setTimeout(async () => {
      if (keyword.length < 2) { setResults([]); setSearchLoading(false); return; }
      try {
        setSearchLoading(true);
        const { data } = await http.get("/api/customer/products", {
          params: { search: keyword, per_page: 7 },
        });
        setResults(data?.data || []);
      } catch {
        setResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  /* ── Close search on outside click ── */
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setQuery(""); setResults([]); setSearchLoading(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* ── Helpers ── */
  const isActive         = (path) => loc.pathname === path;
  const handleGetStarted = () => { setMobileOpen(false); navigate("/login"); };
  const handleExitGuest  = () => { clearGuest(); setMobileOpen(false); navigate("/login"); };
  const handleCart       = () => { setMobileOpen(false); navigate("/cart"); };

  const handleProductClick = (slug) => {
    if (!slug) return;
    navigate(`/product/${slug}`);
    setQuery(""); setResults([]); setSearchLoading(false); setMobileOpen(false);
  };

  const clearSearch = () => { setQuery(""); setResults([]); setSearchLoading(false); };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Escape") { clearSearch(); return; }
    if (e.key === "Enter" && query.trim()) {
      navigate(`/explore?search=${encodeURIComponent(query.trim())}`);
      clearSearch();
    }
  };

  /* ── Search dropdown (shared desktop + mobile) ── */
  const SearchDropdown = () =>
    query.trim().length >= 2 ? (
      <div className="hdr-search-dropdown">
        {searchLoading ? (
          <div className="hdr-search-empty">Searching...</div>
        ) : results.length > 0 ? (
          results.slice(0, 7).map((p, i) => (
            <button
              key={p.id || i}
              type="button"
              className="hdr-search-item"
              onClick={() => handleProductClick(p.slug || p.id)}
            >
              <span className="hdr-search-item-icon"><SearchIcon /></span>
              <span className="hdr-search-item-name">{p.name || p.title || "Product"}</span>
              <span className="hdr-search-item-arrow"><ArrowUpRightIcon /></span>
            </button>
          ))
        ) : (
          <div className="hdr-search-empty">No products found</div>
        )}
      </div>
    ) : null;

  return (
    <>
      <header className={`hdr-root ${scrolled ? "hdr-scrolled" : ""}`}>
        <div className="hdr-container">

          {/* Logo */}
          <Link to="/" className="hdr-logo" onClick={() => setMobileOpen(false)}>
            <img src={logoPng} alt="Easy World Online" className="hdr-logo-img"/>
          </Link>

          {/* Desktop nav */}
          <nav className="hdr-nav">
            {NAV_LINKS.map(({ to, label }) => (
              <Link
                key={to} to={to}
                className={`hdr-nav-link ${isActive(to) ? "active" : ""}`}
                onClick={() => setMobileOpen(false)}
              >
                {label}
                <span className="hdr-nav-link-bar"/>
              </Link>
            ))}
          </nav>

          {/* Desktop actions */}
          <div className="hdr-actions">

            {/* Search */}
            <div className="hdr-search-wrap" ref={searchRef}>
              <div className={`hdr-search ${query ? "focused" : ""}`}>
                <span className="hdr-search-icon"><SearchIcon /></span>
                <input
                  className="hdr-search-input"
                  type="text"
                  placeholder="Search products…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                />
                {query && (
                  <button type="button" className="hdr-search-clear" onClick={clearSearch}>✕</button>
                )}
              </div>
              <SearchDropdown />
            </div>

            {/* Cart (guest + user) */}
            {showCart && (
              <CartButton count={cartCount} onClick={handleCart} />
            )}

            {/* Desktop CTA */}
            <div className="hdr-desktop-cta">
              {isGuest ? (
                <GuestActions onLogin={handleGetStarted} onExit={handleExitGuest} />
              ) : (
                <button className="hdr-btn hdr-btn-primary" onClick={handleGetStarted}>
                  Get Started <ArrowRightIcon/>
                </button>
              )}
            </div>

            {/* Hamburger */}
            <button
              className="hdr-mobile-toggle"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <CloseIcon/> : <MenuIcon/>}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="hdr-mobile-overlay" onClick={() => setMobileOpen(false)}>
          <div className="hdr-mobile-panel" onClick={(e) => e.stopPropagation()}>

            <div className="hdr-mobile-brand">
              <img src={logoPng} alt="Easy World" className="hdr-mobile-brand-logo"/>
            </div>

            <div className="hdr-mobile-divider"/>

            {/* Mobile search */}
            <div className="hdr-mobile-search-block">
              <div className="hdr-search-wrap">
                <div className={`hdr-search ${query ? "focused" : ""}`}>
                  <span className="hdr-search-icon"><SearchIcon /></span>
                  <input
                    className="hdr-search-input"
                    type="text"
                    placeholder="Search products…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                  />
                  {query && (
                    <button type="button" className="hdr-search-clear" onClick={clearSearch}>✕</button>
                  )}
                </div>
                <SearchDropdown />
              </div>
            </div>

            <div className="hdr-mobile-divider"/>

            <nav className="hdr-mobile-nav">
              {NAV_LINKS.map(({ to, label }) => (
                <Link
                  key={to} to={to}
                  className={`hdr-mobile-link ${isActive(to) ? "active" : ""}`}
                  onClick={() => setMobileOpen(false)}
                >
                  {label}
                </Link>
              ))}
            </nav>

            <div className="hdr-mobile-divider"/>

            <div className="hdr-mobile-cta">
              {isGuest ? (
                <>
                  {/* Cart row in mobile guest mode */}
                  <button
                    className="hdr-mobile-cart-row"
                    onClick={handleCart}
                    style={{
                      display       : "flex",
                      alignItems    : "center",
                      justifyContent: "space-between",
                      width         : "100%",
                      padding       : "11px 14px",
                      background    : "#fff4ee",
                      border        : "1.5px solid #ffe0cc",
                      borderRadius  : "10px",
                      marginBottom  : "10px",
                      cursor        : "pointer",
                      fontFamily    : "inherit",
                    }}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13.5px", fontWeight: "700", color: "#f77321" }}>
                      <CartIconSvg /> View Cart
                    </span>
                    {cartCount > 0 && (
                      <span style={{ background: "#f77321", color: "#fff", fontSize: "11px", fontWeight: "800", borderRadius: "12px", padding: "2px 9px" }}>
                        {cartCount} {cartCount === 1 ? "item" : "items"}
                      </span>
                    )}
                  </button>

                  <button className="hdr-btn hdr-btn-primary hdr-btn-full" onClick={handleGetStarted}>
                    Login to your account <ArrowRightIcon/>
                  </button>
                  <button className="hdr-exit-btn-full" onClick={handleExitGuest}>
                    <ExitIcon/> Exit Guest Mode
                  </button>
                </>
              ) : (
                <>
                  <p className="hdr-mobile-cta-text">
                    Join Easy World to explore thousands of products.
                  </p>
                  <button className="hdr-btn hdr-btn-primary hdr-btn-full" onClick={handleGetStarted}>
                    Get Started <ArrowRightIcon/>
                  </button>
                  <button className="hdr-btn hdr-btn-outline hdr-btn-full" onClick={handleGetStarted}>
                    Log in
                  </button>
                </>
              )}
            </div>

          </div>
        </div>
      )}
    </>
  );
}